import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'
import {
  ConfirmVendorAccountCollectionFlowInput,
  VendorAccountCollectionFlowConfirmationResponse,
} from 'src/queries/vendors/vendors.types'
import {
  getBackendErrorPayload,
  NETWORK_ERROR_PAYLOAD,
} from 'src/utils/error.utils'

const CONFIRMATION_RETRY_DELAYS_MS = [1_000, 2_000, 4_000] as const

export type ConfirmationErrorKind =
  | 'stale-or-terminal'
  // SPIKE: a 409 that is NOT terminal. The swap lost the Vendor lock race twice; the API has
  // already retried once itself, so the honest affordance is "try again", not "start over".
  | 'concurrent-update'
  | 'not-found'
  | 'acknowledgement-required'
  | 'provider'
  | 'unexpected'

type ConfirmedVendorAccountCollectionFlowResponse = Extract<
  VendorAccountCollectionFlowConfirmationResponse,
  { status: 'CONFIRMED' }
>

export interface UseVendorAccountConfirmationResult {
  startConfirmation: (input: ConfirmVendorAccountCollectionFlowInput) => void
  phase: 'idle' | 'confirming' | 'pending' | 'error'
  errorKind: ConfirmationErrorKind | null
  isInFlight: boolean
  canRetry: boolean
  retry: () => void
  reset: () => void
  /**
   * Cancel any scheduled retry and invalidate the in-flight attempt WITHOUT
   * touching React state — for callers that need the sequence stopped while
   * the UI it belongs to is still on screen (e.g. a flyout mid-close, which
   * must keep rendering its last frame). `reset()` is the same cancellation
   * plus the state clear; use that once the UI is gone.
   */
  abort: () => void
}

export interface UseVendorAccountConfirmationArgs {
  confirmFlow: (
    input: ConfirmVendorAccountCollectionFlowInput,
  ) => Promise<VendorAccountCollectionFlowConfirmationResponse>
  onConfirmed: (response: ConfirmedVendorAccountCollectionFlowResponse) => void
}

function errorStatus(error: unknown): number | undefined {
  if (typeof error !== 'object' || error === null) return undefined

  if ('status' in error && typeof error.status === 'number') {
    return error.status
  }

  if (
    'response' in error &&
    typeof error.response === 'object' &&
    error.response !== null &&
    'status' in error.response &&
    typeof error.response.status === 'number'
  ) {
    return error.response.status
  }

  return undefined
}

/**
 * SPIKE: reads the coded error body. Needed because status alone stopped being enough to classify
 * a 409 on this route once the swap could lose a lock race — see {@link errorKindFor}.
 */
function errorCodeOf(error: unknown): string | undefined {
  // Axios puts the body on `response.data`; a normalized QueryError has no `.response` and is
  // itself the payload, with the axios error on `originalError` (see src/queries/AGENTS.md).
  // Handling only the first shape would make this read the network fallback for every QueryError
  // and silently classify a retryable conflict as terminal.
  const raw =
    (error as { originalError?: unknown } | null)?.originalError ?? error
  const fromResponse = getBackendErrorPayload(raw)
  if (fromResponse !== NETWORK_ERROR_PAYLOAD) return fromResponse.errorCode
  if (error && typeof error === 'object' && 'errorCode' in error) {
    return (error as { errorCode?: string }).errorCode
  }
  return undefined
}

function errorKindFor(error: unknown): ConfirmationErrorKind {
  switch (errorStatus(error)) {
    case 409:
      // SPIKE: every other 409 on this route really is terminal — a stale flow, a mismatched
      // owner — and offering Retry on those walks the user into a loop. VENDOR_CONCURRENT_UPDATE
      // is the one exception: it means two lifecycle operations collided, which the next attempt
      // very likely wins. Classifying it as terminal would tell the user to start over from an
      // error that clears itself.
      return errorCodeOf(error) === 'VENDOR_CONCURRENT_UPDATE'
        ? 'concurrent-update'
        : 'stale-or-terminal'
    case 404:
      return 'not-found'
    case 422:
      return 'acknowledgement-required'
    case 502:
      return 'provider'
    default:
      return 'unexpected'
  }
}

function isConfirmedWithAchMethod(
  response: VendorAccountCollectionFlowConfirmationResponse,
): response is ConfirmedVendorAccountCollectionFlowResponse {
  return (
    response.status === 'CONFIRMED' &&
    typeof response.paymentMethod?.id === 'string' &&
    response.paymentMethod.id.trim() !== '' &&
    response.paymentMethod.type === 'ACH'
  )
}

/**
 * Owns the post-consent Vendor confirmation sequence. Nothing starts until
 * `startConfirmation` is called by the flyout's explicit primary action.
 */
export default function useVendorAccountConfirmation({
  confirmFlow,
  onConfirmed,
}: UseVendorAccountConfirmationArgs): UseVendorAccountConfirmationResult {
  const [phase, setPhase] =
    useState<UseVendorAccountConfirmationResult['phase']>('idle')
  const [errorKind, setErrorKind] = useState<ConfirmationErrorKind | null>(null)
  const [isInFlight, setIsInFlight] = useState(false)
  const [canRetry, setCanRetry] = useState(false)

  const callbacksRef = useRef({ confirmFlow, onConfirmed })
  useLayoutEffect(() => {
    callbacksRef.current = { confirmFlow, onConfirmed }
  })

  const mountedRef = useRef(true)
  const sequenceIdRef = useRef(0)
  const sequenceStartedRef = useRef(false)
  const requestRef = useRef<ConfirmVendorAccountCollectionFlowInput | null>(
    null,
  )
  const inFlightRef = useRef(false)
  const canRetryRef = useRef(false)
  const retryIndexRef = useRef(0)
  const retryTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const clearRetryTimeout = useCallback((): void => {
    if (retryTimeoutRef.current === null) return
    clearTimeout(retryTimeoutRef.current)
    retryTimeoutRef.current = null
  }, [])

  const updateCanRetry = useCallback((value: boolean): void => {
    canRetryRef.current = value
    setCanRetry(value)
  }, [])

  const attemptConfirmation = useCallback(
    async function attempt(sequenceId: number): Promise<void> {
      const request = requestRef.current
      if (
        !mountedRef.current ||
        sequenceId !== sequenceIdRef.current ||
        !request ||
        inFlightRef.current
      ) {
        return
      }

      inFlightRef.current = true
      setIsInFlight(true)
      setPhase('confirming')
      setErrorKind(null)
      updateCanRetry(false)

      let response: VendorAccountCollectionFlowConfirmationResponse
      try {
        response = await callbacksRef.current.confirmFlow(request)
      } catch (error) {
        if (!mountedRef.current || sequenceId !== sequenceIdRef.current) {
          return
        }

        const nextErrorKind = errorKindFor(error)
        setErrorKind(nextErrorKind)
        setPhase('error')
        updateCanRetry(nextErrorKind !== 'stale-or-terminal')
        return
      } finally {
        if (mountedRef.current && sequenceId === sequenceIdRef.current) {
          inFlightRef.current = false
          setIsInFlight(false)
        }
      }

      if (!mountedRef.current || sequenceId !== sequenceIdRef.current) return

      if (response.status === 'PENDING') {
        setPhase('pending')
        const nextDelay = CONFIRMATION_RETRY_DELAYS_MS[retryIndexRef.current]
        if (nextDelay === undefined) {
          updateCanRetry(true)
          return
        }

        retryIndexRef.current += 1
        retryTimeoutRef.current = setTimeout(() => {
          retryTimeoutRef.current = null
          void attempt(sequenceId)
        }, nextDelay)
        return
      }

      if (!isConfirmedWithAchMethod(response)) {
        setErrorKind('unexpected')
        setPhase('error')
        updateCanRetry(true)
        return
      }

      setPhase('idle')
      setErrorKind(null)
      updateCanRetry(false)
      callbacksRef.current.onConfirmed(response)
    },
    [updateCanRetry],
  )

  const startConfirmation = useCallback(
    (input: ConfirmVendorAccountCollectionFlowInput): void => {
      if (sequenceStartedRef.current) return

      sequenceStartedRef.current = true
      requestRef.current = input
      retryIndexRef.current = 0
      clearRetryTimeout()
      const sequenceId = sequenceIdRef.current + 1
      sequenceIdRef.current = sequenceId
      void attemptConfirmation(sequenceId)
    },
    [attemptConfirmation, clearRetryTimeout],
  )

  const retry = useCallback((): void => {
    if (!requestRef.current || inFlightRef.current || !canRetryRef.current) {
      return
    }

    clearRetryTimeout()
    retryIndexRef.current = 0
    updateCanRetry(false)
    const sequenceId = sequenceIdRef.current + 1
    sequenceIdRef.current = sequenceId
    void attemptConfirmation(sequenceId)
  }, [attemptConfirmation, clearRetryTimeout, updateCanRetry])

  // Bumping sequenceIdRef is what actually stops the sequence: every
  // continuation re-checks it, so a scheduled retry and any resolving request
  // both become no-ops.
  const abort = useCallback((): void => {
    sequenceIdRef.current += 1
    clearRetryTimeout()
    sequenceStartedRef.current = false
    requestRef.current = null
    inFlightRef.current = false
    retryIndexRef.current = 0
    canRetryRef.current = false
  }, [clearRetryTimeout])

  const reset = useCallback((): void => {
    abort()
    setPhase('idle')
    setErrorKind(null)
    setIsInFlight(false)
    setCanRetry(false)
  }, [abort])

  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
      sequenceIdRef.current += 1
      clearRetryTimeout()
    }
  }, [clearRetryTimeout])

  return {
    startConfirmation,
    phase,
    errorKind,
    isInFlight,
    canRetry,
    retry,
    reset,
    abort,
  }
}
